import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useAbility } from '../../../contexts/AbilityContext';
import LoadingSpinner from '../LoadingSpinner';

const ProtectedRoute = ({ 
  children, 
  action = null, 
  subject = null, 
  field = null, 
  redirectTo = "/unauthorized" 
}) => {
  const { isAuthenticated, authInProgress } = useAuth();
  const ability = useAbility();
  const location = useLocation();
  const [permissionChecked, setPermissionChecked] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);

  // Check permissions when abilities are applied
  useEffect(() => {
    // Check if this route requires specific permissions
    const requiresSpecificPermissions = action !== null && subject !== null;
    
    if (isAuthenticated) {
      if (requiresSpecificPermissions) {
        // Check specific permissions
        const canAccess = ability.can(action, subject, field);

        setHasPermission(canAccess);
        setPermissionChecked(true);
      } else {
        // If no specific permission is required, just need authentication
        setHasPermission(true);
        setPermissionChecked(true);
      }
    }
  }, [isAuthenticated, ability, action, subject, field]);

  // Show loading spinner while authentication is in progress or permissions are loading
  if (isAuthenticated && authInProgress) {
    return <LoadingSpinner message="Loading permissions... protected route" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If permissions have been checked and user doesn't have permission
  if (permissionChecked && !hasPermission) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // If permissions have been checked and user has permission
  if (permissionChecked && hasPermission) {
    return children;
  }

  // Still checking permissions
  return <LoadingSpinner message="Verifying access..." />;
};

export default ProtectedRoute; 